import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { Router } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { first, takeUntil, tap } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import {
  ActivitySubscriptionsCompanyComponent,
  ActivitySubscriptionsFollowedByMeOfferComponent,
  ActivitySubscriptionsFollowOfferComponent,
} from '@recruitee/activities-and-notifications';
import { ActivitySubscriptionType } from '@recruitee/activities-types';
import type { ActivitySubscriptionUpdateData } from '@recruitee/activities-types';
import { APP_CONFIG } from '@recruitee/common';
import { FeatureGuardFacade, GuardedFeature } from '@recruitee/feature-guard';
import { HttpApiService } from '@recruitee/http';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { SlackIntegration, SlackIntegrationHttp } from '@recruitee/slack-integration-data';
import {
  SlackIntegrationActions,
  SlackIntegrationSelectors,
} from '@recruitee/slack-integration-data';
import {
  ButtonComponent,
  ConfirmDialogVariant,
  DialogService,
  DividerComponent,
  IconComponent,
  TabComponent,
  TabsComponent,
} from '@recruitee/user-interface';

@Component({
  selector: 'rt-slack-settings-container',
  templateUrl: './slack-settings-container.component.html',
  styleUrls: ['./slack-settings-container.component.less'],
  imports: [
    TabComponent,
    TabsComponent,
    TranslatePipe,
    ActivitySubscriptionsFollowOfferComponent,
    ActivitySubscriptionsCompanyComponent,
    ActivitySubscriptionsFollowedByMeOfferComponent,
    ButtonComponent,
    DividerComponent,
    IconComponent,
  ],
})
export class SlackSettingsContainerComponent implements OnInit, OnDestroy {
  public readonly ActivitySubscriptionType = ActivitySubscriptionType;

  public abilities = {
    viewOffers: false,
    viewMailbox: false,
    manageCandidates: false,
    viewCandidates: false,
    manageJobsReferralSettings: false,
    viewAllOffers: false,
    manageRecruiters: false,
    shareCandidates: false,
    manageReferrals: false,
    manageAttributes: false,
    manageDisqualifyReasons: false,
    manageTexting: false,
    viewRequisitions: false,
    manageLocations: false,
    viewAcquisition: false,
    manageJobTemplates: false,
  };

  public hasRequisitions = false;

  public slackIntegrations: SlackIntegrationHttp[] = [];
  public slackIntegration: SlackIntegration | null;
  public slackIntegrationsLoading: boolean = false;
  public deleting: boolean = false;

  public removeActionConfig = {
    handler: () => {
      //
    },
  };

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private featureGuard: FeatureGuardFacade,
    protected httpApi: HttpApiService,
    @Inject(APP_CONFIG) private appConfig: { authUrl: string },
    private dialogService: DialogService,
    private i18n: I18nService,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.checkAbilities();
    this.checkRequisitionsFeature();
    this.loadSlackIntegrations();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public integrate(): void {
    //
  }
  public remove(): void {
    //
  }

  public loadSlackIntegrations(): void {
    this.store.dispatch(SlackIntegrationActions.fetchCollection());
    this.store
      .select(SlackIntegrationSelectors.allEntities)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(slackIntegrations => {
        const currentIntegration = this.slackIntegration
          ? slackIntegrations!.find(integration => integration.id === this.slackIntegration!.id)
          : null;

        let slackIntegration: SlackIntegration | null;
        if (currentIntegration) {
          slackIntegration = currentIntegration;
        } else if (slackIntegrations!.length > 0) {
          slackIntegration = slackIntegrations![0];
        } else {
          slackIntegration = null;
        }

        this.slackIntegrations = slackIntegrations!;
        this.slackIntegration = this.resolveSlackIntegration(slackIntegration);
        this.cdRef.detectChanges();
      });

    this.store
      .select(SlackIntegrationSelectors.isPending({ scope: 'main' }))
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(loading => {
        this.slackIntegrationsLoading = loading;
        this.cdRef.detectChanges();
      });
  }

  public addChannel(): void {
    window.location.href = `${this.appConfig.authUrl}c/${this.httpApi.currentCompanyId}/integrations?integration=slack`;
  }

  public selectSlackIntegration(slackIntegration: SlackIntegration): void {
    this.slackIntegration = this.resolveSlackIntegration(slackIntegration);
    this.cdRef.detectChanges();
  }

  public changeEvents({ events, value }: ActivitySubscriptionUpdateData): void {
    const slackIntegrationId = this.slackIntegration!.id;
    const eventsMap: any = new Map();
    events.map(event => (eventsMap[event] = { slack: value }));

    this.store.dispatch(
      SlackIntegrationActions.updateOnePessimistic({
        payload: {
          data: {
            id: slackIntegrationId,
            activitySubscriptions: {
              ...eventsMap,
            },
            channelName: this.slackIntegration!.channelName,
          },
        },
      }),
    );
  }

  public deleteSlackIntegration(slackIntegration: SlackIntegration): void {
    if (this.deleting) return;

    this.dialogService
      .confirm({
        variant: ConfirmDialogVariant.Error,
        title: this.i18n.translate('app.settings.integrations.slack.remove_dialog.title'),
        message: this.i18n.translate('app.settings.integrations.slack.remove_dialog.message', {
          channel_name: slackIntegration.channelName,
        }),
        agreeLabel: this.i18n.translate('app.common.remove'),
        cancelLabel: this.i18n.translate('app.common.cancel'),
      })
      .pipe(
        tap(confirmed => {
          if (confirmed) {
            this.deleting = true;
            this.cdRef.detectChanges();
            this.store.dispatch(
              SlackIntegrationActions.removeOnePessimistic({
                payload: {
                  data: slackIntegration.id,
                },
              }),
            );
          }
        }),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(() => {
        this.deleting = false;
        this.cdRef.detectChanges();
      });
  }

  public trackByIntegration(_index: number, integration: SlackIntegration): string {
    return integration.channelName;
  }

  public get actionsDisabled(): boolean {
    return this.deleting || this.slackIntegrationsLoading;
  }

  private checkRequisitionsFeature(): void {
    this.featureGuard
      .checkFeatureAvailability(GuardedFeature.Requisitions)
      .pipe(first())
      .subscribe(areRequisitionsAvailable => {
        this.hasRequisitions = areRequisitionsAvailable;
        this.cdRef.detectChanges();
      });
  }

  private checkAbilities(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$))
      .subscribe(abilityMap => {
        this.abilities = {
          viewOffers: abilityMap[Ability.ViewJobs],
          viewMailbox: abilityMap[Ability.ViewMailbox],
          manageCandidates: abilityMap[Ability.ManageCandidates],
          viewCandidates: abilityMap[Ability.ViewCandidates],
          manageJobsReferralSettings: abilityMap[Ability.ManageJobsReferralSettings],
          viewAllOffers: abilityMap[Ability.ViewAllJobs],
          manageRecruiters: abilityMap[Ability.ManageRecruiters],
          shareCandidates: abilityMap[Ability.ShareCandidates],
          manageReferrals: abilityMap[Ability.ManageReferrals],
          manageAttributes: abilityMap[Ability.ManageAttributes],
          manageDisqualifyReasons: abilityMap[Ability.ManageDisqualifyReasons],
          manageTexting: abilityMap[Ability.ManageTexting],
          viewRequisitions: abilityMap[Ability.ViewRequisitions],
          manageLocations: abilityMap[Ability.ManageLocations],
          viewAcquisition: abilityMap[Ability.ViewAcquisition],
          manageJobTemplates: abilityMap[Ability.ManageJobTemplates],
        };

        this.cdRef.detectChanges();
      });
  }

  private resolveSlackIntegration(
    slackIntegration: SlackIntegration | null,
  ): SlackIntegration | null {
    if (!slackIntegration) return null;
    if (slackIntegration.activitySubscriptions) {
      delete slackIntegration.activitySubscriptions.my;
    }
    return slackIntegration;
  }
}
